export const ADMIN_EMPLOYEE_ROLE = "admin_employee";
export const BASIC_EMPLOYEE_ROLE = "basic_employee";
export const RESTRICTED_EMPLOYEE_ROLE = "restricted_employee";

export type EmployeeRole =
  | typeof ADMIN_EMPLOYEE_ROLE
  | typeof BASIC_EMPLOYEE_ROLE
  | typeof RESTRICTED_EMPLOYEE_ROLE;

// Employees whose data is scoped to their own leads/appointments + assigned pipelines.
export const isDataScopedEmployee = (roles: string[]): boolean =>
  roles.includes(BASIC_EMPLOYEE_ROLE) || roles.includes(RESTRICTED_EMPLOYEE_ROLE);

// The most restricted role: only the allowed paths below are reachable.
export const isRestrictedEmployee = (roles: string[]): boolean =>
  roles.includes(RESTRICTED_EMPLOYEE_ROLE);

// Paths a restricted employee may reach.
export const RESTRICTED_EMPLOYEE_ALLOWED_PATHS = ["/activities", "/inbox", "/tablero"];

// The only merchant with access to the portal-codes CSV upload (Configuration > Códigos de Portales).
export const PORTAL_CODES_MERCHANT_ID = "albertoalvarezventa";

// The Palomma-only gate (Agentes / Proyectos, MercadoLibre upload) has no copy here: its
// external allowlist is a backend secret, so the portal reads the decision from
// auth.getUserInfo (UserContext.hasPalommaOnlyAccess).

// The "@" anchors the suffix: "attacker@notpalomma.com" must not pass. The one place the
// fianza gate below decides "this address is Palomma staff".
const isPalommaStaffEmail = (normalizedEmail: string): boolean =>
  normalizedEmail.endsWith("@palomma.com");

const normalizeEmail = (email: string | undefined): string | undefined => {
  const normalized = email?.trim().toLowerCase();
  return normalized ? normalized : undefined;
};

// Every fianza position, in funnel order — the study and both verdicts — all Palomma's to move a
// lead into. "Fianza en estudio" is gated with the verdicts: it says Palomma has a credit study
// under way, and a merchant's advisor must not assert that by dragging a card. Filing a request
// still moves the lead there automatically on the backend, which does not go through this gate.
// Restated rather than imported: `@agent/*` is a TypeScript-only alias, so the portal cannot pull
// the backend's SYSTEM_POSITIONS_MAP at runtime. MUST stay in sync with
// RENT_GUARANTEE_POSITION_IDS in packages/agent/src/helpers/permissions.ts.
export const RENT_GUARANTEE_POSITION_IDS = [
  "rentGuaranteeInReview",
  "rentGuaranteePreApproved",
  "rentGuaranteeApproved",
];

// The subset that records an outcome — see pipelineOffersRentGuarantee. Listed rather than
// derived from the set above: each list should say what is in it without sending the reader to
// the other one.
const RENT_GUARANTEE_VERDICT_POSITION_IDS = ["rentGuaranteePreApproved", "rentGuaranteeApproved"];

// Non-Palomma emails cleared to move leads into the fianza positions. Empty on purpose: the
// first cut is the @palomma.com domain alone. Mirrors RENT_GUARANTEE_MOVE_ALLOWLIST in the
// backend copy — widen both together or the UI offers a move the endpoint rejects.
export const RENT_GUARANTEE_MOVE_ALLOWLIST: string[] = [];

export const isRentGuaranteePosition = (pipelinePositionId: string): boolean =>
  RENT_GUARANTEE_POSITION_IDS.includes(pipelinePositionId);

// Whether the lead's current position puts it in the fianza funnel. The same set as
// isRentGuaranteePosition — every fianza position is gated — but kept as its own name because the
// question here is "is this lead a fianza case" (may an analyst record its cosigners?), not
// "may this caller move a lead here".
export const isInRentGuaranteeFunnel = isRentGuaranteePosition;

/**
 * Whether this address may act on Palomma's fianza — move a lead into any fianza position, or
 * record the cosigners behind it.
 *
 * The @palomma.com domain plus RENT_GUARANTEE_MOVE_ALLOWLIST, and nothing else. Deliberately NOT
 * UserContext.hasPalommaOnlyAccess: that flag carries the PALOMMA_ONLY_ACCESS_ALLOWLIST secret,
 * which opens internal configuration screens to named partners, and adding someone there must
 * never hand them the fianza as a side effect. Mirrors canManageRentGuarantee in
 * packages/agent/src/helpers/permissions.ts — widen both together or the UI offers what the
 * endpoint rejects.
 */
export const canManageRentGuarantee = (email: string | undefined): boolean => {
  const normalized = normalizeEmail(email);
  if (!normalized) return false;
  return isPalommaStaffEmail(normalized) || RENT_GUARANTEE_MOVE_ALLOWLIST.includes(normalized);
};

// Whether a pipeline can take a lead through a fianza at all. Decides whether the lead profile
// offers to file a request; the endpoint (leads.requestRentGuarantee) applies the same test.
// Deliberately the verdict positions only: a pipeline carrying "Fianza en estudio" alone could
// park a lead on the study with no way to resolve it.
export const pipelineOffersRentGuarantee = (positions: readonly { id: string }[]): boolean =>
  positions.some((position) => RENT_GUARANTEE_VERDICT_POSITION_IDS.includes(position.id));

// Moving a lead into a fianza position mints a credit-case number, issues a certificate in
// Palomma's name and notifies the customer — Palomma's underwriting, not the merchant's. The
// restriction is one-way: leads may always be moved back OUT of those positions.
export const canMoveLeadToPipelinePosition = (input: {
  email: string | undefined;
  pipelinePositionId: string;
}): boolean => {
  if (!isRentGuaranteePosition(input.pipelinePositionId)) return true;

  return canManageRentGuarantee(input.email);
};

export const canReassignLead = (input: {
  callerEmail: string | undefined;
  leadReassignmentEnabled: boolean | undefined;
  leadReassignmentAllowedEmails: string[] | undefined;
}): boolean => {
  if (input.leadReassignmentEnabled === false) return false;
  if (!input.leadReassignmentAllowedEmails || input.leadReassignmentAllowedEmails.length === 0) {
    return true;
  }
  if (!input.callerEmail) return false;
  const caller = input.callerEmail.toLowerCase();
  return input.leadReassignmentAllowedEmails.map((e) => e.toLowerCase()).includes(caller);
};
